import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { GOOGLE_TAG_ID } from './consts'
import Layout from './layouts/Layout'
import { useMDXComponents } from './mdx-components'
import Home from './pages/index'

const About = lazy(() => import('./pages/about'))
const Company = lazy(() => import('./pages/company.mdx'))
const Contact = lazy(() => import('./pages/contact'))
const CookiePolicy = lazy(() => import('./pages/cookie-policy'))
const Portfolio = lazy(() => import('./pages/portfolio.mdx'))
const PrivacyPolicyV1 = lazy(() => import('./pages/privacy-policy/v1.mdx'))
const Posts = lazy(() => import('./pages/posts/index'))
const PostPage = lazy(() => import('./pages/posts/PostPage'))
const Services = lazy(() => import('./pages/services.mdx'))
const Support = lazy(() => import('./pages/support.mdx'))
const TermsOfService = lazy(() => import('./pages/terms-of-service.mdx'))

const NotFound = () => (
  <section className='py-16'>
    <h1 className='text-3xl font-bold text-white'>Page not found</h1>
    <p className='mt-4 text-gray-300'>The page you requested does not exist.</p>
  </section>
)

export const App = () => {
  const mdxComponents = useMDXComponents({})

  useEffect(() => {
    document.documentElement.classList.add('dark')

    if (!GOOGLE_TAG_ID) {
      return
    }

    const script = document.createElement('script')
    script.id = 'gtm-script'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GOOGLE_TAG_ID}`
    document.head.appendChild(script)

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  return (
    <Layout>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GOOGLE_TAG_ID}`}
          height='0'
          width='0'
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>

      <Suspense fallback={<p className='py-8 text-gray-300'>Loading...</p>}>
        <Routes>
          <Route path='/' element={<Home />} />
          <Route path='/about' element={<About />} />
          <Route path='/company' element={<Company components={mdxComponents} />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/cookie-policy' element={<CookiePolicy />} />
          <Route path='/portfolio' element={<Portfolio components={mdxComponents} />} />
          <Route path='/services' element={<Services components={mdxComponents} />} />
          <Route path='/support' element={<Support components={mdxComponents} />} />
          <Route path='/terms-of-service' element={<TermsOfService components={mdxComponents} />} />
          <Route path='/privacy-policy'>
            <Route path='v1' element={<PrivacyPolicyV1 components={mdxComponents} />} />
            <Route index element={<Navigate to='v1' replace />} />
          </Route>
          <Route path='/posts' element={<Posts />} />
          <Route path='/posts/:slug' element={<PostPage />} />
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}
